import React, { FC, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Divider, Flex, Segmented, Skeleton, Tooltip, Typography } from 'antd'
import { EmptyState, Well } from '@pushly/aqe/lib/components'
import { PQueryBuilder } from '@pushly/aqe/lib/components/pquery-builder'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { getClassNames } from '../../../_utils/classnames'
import { InfoRow } from './brand-voice-decomposition'
import { DISPLAY_TIER_ORDER, tierCounts, type BrandVoiceDisplayTier } from './brand-voice-tier-visual'
import { CONTENT_DIRECTIVE_FIELDS } from '../content-directive-fields'
import { collapsedGuaranteedSpec, guaranteedDisplayRows } from './text-highlight'

/**
 * The settings-page view of a saved Brand Voice.
 *
 * Split out of {@link BrandVoiceWell} so it can be rendered and tested without the IoC-bound
 * services the well needs to fetch and edit.
 *
 * TEXT LEFT, RULES RIGHT: the same arrangement as the editor drawer, so this reads as a smaller
 * view of the same thing rather than a second, differently-shaped one.
 *
 * One tier shown at a time, chosen by `activeTier` — a controlled prop, not local state, because
 * the tags that switch it live in the Directive well's own header (next to Edit), above this
 * component entirely. `BrandVoiceWell` owns the selection so the header tag styling and this
 * pane's content can never disagree about which tier is "on".
 */
const TIER_LABEL: Record<BrandVoiceDisplayTier, string> = {
    guaranteed: 'Enforced',
    guidance: 'Guidance',
}

/** What each tier means, on hover over its option, so the two words the feature turns on are never left undefined. */
const TIER_DEFINITION: Record<BrandVoiceDisplayTier, string> = {
    guaranteed: 'Checked on every send.',
    guidance: 'Steers the tone, but is not checked.',
}

/**
 * Held constant across BOTH columns and every tier's pane: switching tiers must never resize the card. A floor alone
 * (`min-height`) does not do this, since nothing stops the card from growing past it and shrinking back on the next
 * switch. A true fixed height does: content that would overflow it scrolls inside the pane instead.
 */
// Nudged up from 220: content that landed just a few pixels past the old value triggered a
// scrollbar for barely-there overflow, which read as a layout bug more than a real cap.
const PANE_COLLAPSED_HEIGHT = 248

/**
 * A column's fixed-height pane, which scrolls what does not fit. A fade over its bottom edge says there is more below,
 * and goes once the end is reached. The fade sits outside the scrolling box, so it does not scroll away with the text.
 */
const ScrollPane: FC<{ children: React.ReactNode; watch: unknown }> = ({ children, watch }) => {
    const ref = useRef<HTMLDivElement>(null)
    const [moreBelow, setMoreBelow] = useState(false)
    const measure = () => {
        const el = ref.current
        if (el) setMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 1)
    }

    useLayoutEffect(() => {
        measure()
        const el = ref.current
        if (!el || typeof ResizeObserver === 'undefined') return

        const observer = new ResizeObserver(measure)
        observer.observe(el)
        return () => observer.disconnect()
    }, [watch])

    return (
        <div className={getClassNames('bvw-pane', { faded: moreBelow })}>
            {/* `paddingRight` keeps the scrollbar off the last character of each line. */}
            <div
                ref={ref}
                style={{ height: PANE_COLLAPSED_HEIGHT, overflowY: 'auto', paddingRight: 12 }}
                onScroll={measure}
            >
                {children}
            </div>
        </div>
    )
}

interface IBrandVoiceSummary {
    body: string
    /** Null for a directive saved before `compiled_rules` was reliably persisted, or mid-first
     * compile — there is nothing to summarize, so the body renders on its own. */
    compiled: CompiledDirective | null
    activeTier: BrandVoiceDisplayTier
    /** Switches the rules column to another tier. The text column is the same for every tier. */
    onTierChange: (tier: BrandVoiceDisplayTier) => void
}

export const BrandVoiceSummary: FC<IBrandVoiceSummary> = ({ body, compiled, activeTier, onTierChange }) => {
    // Guidance spans all three prose tiers — see `DISPLAY_TIER_FOR`. Ordered mandates ->
    // persona -> conditional, matching the drawer and sailfish's `toDirectiveTierView`.
    const guidanceItems = useMemo(
        () => [...(compiled?.assertions ?? []), ...(compiled?.style ?? []), ...(compiled?.unmapped ?? [])],
        [compiled],
    )
    const guaranteedRows = useMemo(() => (compiled ? guaranteedDisplayRows(compiled) : []), [compiled])

    const counts = useMemo(() => tierCounts(compiled), [compiled])

    // PQueryBuilder's own row caps a list value at 3 entries and renders the rest as a bare
    // "+N" span — no tooltip, no click, no way to actually read the rest (confirmed off its
    // compiled source: `rule-row.js`'s `listTokens.slice(0, 3)`). We already have every value
    // before handing it to PQueryBuilder, so rather than forking a third-party row renderer to
    // add a hover, this builds the same "what's behind +N" answer as a wrapping Tooltip instead.
    const guaranteedOverflowTooltip = useMemo(() => {
        const truncatedRows = guaranteedRows.filter((row) => Array.isArray(row.value) && row.value.length > 3)
        if (truncatedRows.length === 0) return null

        return (
            <Flex vertical gap={4}>
                {truncatedRows.map((row) => {
                    const label = CONTENT_DIRECTIVE_FIELDS.find((f) => f.key === row.attribute)?.label ?? row.attribute
                    return (
                        <Typography.Text key={row.key} style={{ color: 'inherit' }}>
                            <strong>{label}:</strong> {(row.value as unknown[]).join(', ')}
                        </Typography.Text>
                    )
                })}
            </Flex>
        )
    }, [guaranteedRows])

    if (!compiled) {
        return (
            <Well
                variant="ghost"
                padding="none"
                titleSize="sm"
                title={<Typography.Text strong>What you wrote</Typography.Text>}
            >
                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{body}</Typography.Paragraph>
            </Well>
        )
    }

    return (
        // TEXT LEFT, RULES RIGHT — the same arrangement as the editor drawer. An earlier pass had
        // them flipped, which meant the two views of one directive put the same two things on
        // opposite sides: moving between them made the reader re-find both.
        // The wrapper is the size container the columns stack against (see `.sw-v2-bvw-summary`):
        // the card's own width, not the viewport's, since the settings rail changes the former.
        <div className={getClassNames('bvw-summary')}>
            <Flex gap={16} align="flex-start" className={getClassNames('bvw-columns')}>
                <Flex className={getClassNames('bvw-column')}>
                    <Well
                        variant="ghost"
                        padding="none"
                        titleSize="sm"
                        style={{ width: '100%' }}
                        title={<Typography.Text strong>What you wrote</Typography.Text>}
                    >
                        {/* Always the full body, never `Typography.Paragraph`'s `ellipsis`, whose row-clamp measurement
                        races inside a flex column and falls back to the untruncated text. A plain box with a real fixed
                        height scrolls it instead, so the card holds one size across tier switches and the text stays
                        beside the rules it came from. */}
                        <ScrollPane watch={body}>
                            <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                                {body}
                            </Typography.Paragraph>
                        </ScrollPane>
                    </Well>
                </Flex>
                <Divider orientation="vertical" className="inner-well-divider vertical" />
                <Flex className={getClassNames('bvw-column')}>
                    <Well
                        variant="ghost"
                        padding="none"
                        titleSize="sm"
                        style={{ width: '100%' }}
                        // Titled like the text column beside it. The tier switch is the column's action, since it
                        // changes this column only.
                        title={<Typography.Text strong>What we compiled</Typography.Text>}
                        actions={
                            <Segmented<BrandVoiceDisplayTier>
                                aria-label="Rule tier"
                                size="small"
                                className={getClassNames('bvw-tier-switch')}
                                value={activeTier}
                                onChange={onTierChange}
                                options={DISPLAY_TIER_ORDER.map((tier) => ({
                                    value: tier,
                                    label: (
                                        <Tooltip title={TIER_DEFINITION[tier]}>
                                            <span>
                                                {TIER_LABEL[tier]} {counts[tier]}
                                            </span>
                                        </Tooltip>
                                    ),
                                }))}
                            />
                        }
                    >
                        <ScrollPane watch={`${activeTier}:${guidanceItems.length}:${guaranteedRows.length}`}>
                            {/* The REAL PQueryBuilder, disabled — not a hand-rolled row. It owns the
                            field labels, the operator phrasing and the value chips, and reusing it
                            is what keeps this card, the editor drawer and the segment builder
                            saying the same sentence for the same condition.
                            Guidance cannot go through it, and that is not a gap: a builder renders
                            a PQuery condition, and Guidance has none. Being unexpressible as a
                            condition is exactly what makes it not-guaranteed. */}
                            {activeTier === 'guaranteed' ? (
                                guaranteedRows.length > 0 ? (
                                    <Tooltip
                                        title={guaranteedOverflowTooltip}
                                        placement="bottom"
                                        // No tooltip at all when nothing is actually hidden — an empty
                                        // Tooltip wrapper still intercepts hover/focus for its own
                                        // (blank) popup, which is worse than no wrapper.
                                        open={guaranteedOverflowTooltip ? undefined : false}
                                    >
                                        <div>
                                            <PQueryBuilder
                                                value={collapsedGuaranteedSpec(compiled)}
                                                fields={CONTENT_DIRECTIVE_FIELDS}
                                                disabled
                                                // `bvw-rules-pqb` drops the builder's own "All of the following are
                                                // true" header here: the column title already says what the rules are.
                                                className={`${getClassNames('bvd-guaranteed-pqb')} ${getClassNames(
                                                    'bvw-rules-pqb',
                                                )}`}
                                                onRuleCommit={() => {}}
                                                // `disabled` above stops the interactions; these blank
                                                // the "Add Rule"/"Add Group" labels — but the buttons
                                                // themselves are plain antd Buttons with no class of
                                                // their own, so blanking the label alone leaves an
                                                // empty, disabled icon button behind rather than hiding
                                                // it. See `.sw-v2-bvd-guaranteed-pqb` in
                                                // brand-voice-decomposition.scss for what actually
                                                // removes them.
                                                labels={{
                                                    connectors: { and: '', or: '' },
                                                    addCondition: '',
                                                    addGroup: '',
                                                }}
                                            />
                                        </div>
                                    </Tooltip>
                                ) : (
                                    <EmptyState size="small" align="start" title="Nothing enforced yet" />
                                )
                            ) : guidanceItems.length > 0 ? (
                                // Every item, in the pane that scrolls: a "+N more" inside a scrolling box hid the same
                                // overflow twice.
                                <Flex vertical gap={8}>
                                    {guidanceItems.map((item, i) => (
                                        <InfoRow key={i}>
                                            <Typography.Text>{item.text}</Typography.Text>
                                        </InfoRow>
                                    ))}
                                </Flex>
                            ) : (
                                <EmptyState size="small" align="start" title="No guidance yet" />
                            )}
                        </ScrollPane>
                    </Well>
                </Flex>
            </Flex>
        </div>
    )
}

/**
 * The summary as it will land, before the Brand Voice has loaded: the same columns, titles and pane height, with
 * placeholders where the text, the tier switch and the rules go, so the card does not change size when they arrive.
 */
export const BrandVoiceSummarySkeleton: FC = () => (
    <div className={getClassNames('bvw-summary')} aria-busy="true" aria-label="Loading your Brand Voice">
        <Flex gap={16} align="flex-start" className={getClassNames('bvw-columns')}>
            <Flex className={getClassNames('bvw-column')}>
                <Well
                    variant="ghost"
                    padding="none"
                    titleSize="sm"
                    style={{ width: '100%' }}
                    title={<Typography.Text strong>What you wrote</Typography.Text>}
                >
                    <div style={{ height: PANE_COLLAPSED_HEIGHT, overflow: 'hidden' }}>
                        <Skeleton
                            active={true}
                            title={false}
                            paragraph={{
                                rows: 9,
                                width: ['100%', '96%', '100%', '88%', '100%', '92%', '100%', '84%', '60%'],
                            }}
                        />
                    </div>
                </Well>
            </Flex>
            <Divider orientation="vertical" className="inner-well-divider vertical" />
            <Flex className={getClassNames('bvw-column')}>
                <Well
                    variant="ghost"
                    padding="none"
                    titleSize="sm"
                    style={{ width: '100%' }}
                    title={<Typography.Text strong>What we compiled</Typography.Text>}
                    actions={<Skeleton.Button active={true} size="small" style={{ width: 170 }} />}
                >
                    <Flex vertical gap={8} style={{ height: PANE_COLLAPSED_HEIGHT, overflow: 'hidden' }}>
                        <Skeleton.Input active={true} block={true} style={{ height: 36 }} />
                        <Skeleton.Input active={true} block={true} style={{ height: 36, width: '80%' }} />
                    </Flex>
                </Well>
            </Flex>
        </Flex>
    </div>
)
